import React, { useState, useEffect } from 'react'
import { Box, Typography, Divider, Icon, useMediaQuery, Skeleton } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'

import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import leaveRequestStatusHandler from 'module/leave/util/leaveRequestStatusHandler'

import { useGetAllFilteredLeaveRequest } from 'services/api/modules/leave/leaveRequest'
import AdxPaginationNew from 'components/reusable/AdxPaginationNew'
import AdxButton from 'components/custom/AdxButton'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const columns = [
  {
    field: 'request',
    headerName: 'Request',
    width: '30%',
  },
  {
    field: 'duration',
    headerName: 'Duration',
    width: '40%',
  },
  {
    field: 'status',
    headerName: 'Status',
    width: '20%',
  },
  {
    field: 'action',
    headerName: '',
    width: '10%',
  },
]

const smallCol = [
  {
    field: 'request',
    headerName: 'Request',
    width: '50%',
  },
  {
    field: 'status',
    headerName: 'Status',
    width: '40%',
  },
  {
    field: 'action',
    headerName: '',
    width: '10%',
  },
]

const AllRequestTable = ({ filter, setPendingCount, setCompletedCount }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const isSmall = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  const isLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'))
  let skeletonArray = new Array(5).fill('i')

  // pagination
  const [currentPage, setCurrentPage] = useState(0)
  const [rowsPerCurrentPage, setRowsPerCurrentPage] = useState(10)
  const handleChangeCurrentPage = (event, newPage) => {
    setCurrentPage(newPage)
  }
  const handleChangeRowsPerCurrentPage = (event) => {
    setRowsPerCurrentPage(parseInt(event.target.value, 10))
    setCurrentPage(0)
  }

  const { data, isLoading, refetch, isRefetching } = useGetAllFilteredLeaveRequest({
    status: filter,
    limit: rowsPerCurrentPage,
    page: currentPage + 1,
  })

  useEffect(() => {
    refetch()
  }, [rowsPerCurrentPage, currentPage])

  useEffect(() => {
    if (data && filter === 1) {
      setPendingCount(data?.data?.length)
    }
  }, [data, filter, setPendingCount])

  if (!isSmall) {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 320px)',
            width: '100%',
            overflow: 'auto',
          }}
        >
          {isLoading || isRefetching ? (
            skeletonArray.map((arr, index) => (
              <React.Fragment key={index}>
                <Box p={2}>
                  <Skeleton width={'100%'} height={50} />
                </Box>
                <Divider light />
              </React.Fragment>
            ))
          ) : (!isLoading || !isRefetching) && data === null ? (
            <Box
              position="absolute"
              width={1}
              top={100}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Typography fontWeight="bold" color="light-gray-text.main">
                No leave request
              </Typography>
            </Box>
          ) : (
            data?.data?.map((item, index) => (
              <React.Fragment key={index}>
                <Box
                  onClick={() =>
                    navigate(`/app/leave/request/${item.id}`, {
                      state: {
                        backgroundLocation: location,
                      },
                    })
                  }
                  sx={{
                    '&:hover': {
                      cursor: 'pointer',
                    },
                  }}
                  display="flex"
                  px={2}
                >
                  <Box
                    sx={{
                      width: smallCol[0].width,
                      py: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      rowGap: 2,
                    }}
                  >
                    <Typography variant="subtitle1" color="dark-text.main">
                      {item.leave_policy_name}
                    </Typography>
                    <Typography variant="subtitle 1" color="alternate.main">
                      {item.duration === 1 ? `${item.duration} day` : `${item.duration} days`}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: smallCol[1].width,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <AdxStatusBar status={leaveRequestStatusHandler(item.status)} />
                  </Box>
                  <Box
                    sx={{
                      width: smallCol[2].width,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Icon sx={{ color: 'alternate.main' }}>arrow_forward_ios</Icon>
                  </Box>
                </Box>
                <Divider />
              </React.Fragment>
            ))
          )}
        </Box>
        <Box
          bgcolor="white.main"
          mb={2}
          display="flex"
          justifyContent="flex-end"
          sx={{
            width: '100%',
            borderBottomRightRadius: 2,
            borderBottomLeftRadius: 2,
          }}
        >
          <AdxPaginationNew
            count={data?.total}
            page={currentPage}
            onPageChange={handleChangeCurrentPage}
            rowsPerPage={rowsPerCurrentPage}
            onRowsPerPageChange={handleChangeRowsPerCurrentPage}
          />
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Box>
        <Box sx={{ display: 'flex', width: '100%', p: 2 }}>
          {columns.map((col) => (
            <Box key={col.headerName} sx={{ width: col.width }}>
              <Typography variant="subtitle2" color="light-gray-text.main">
                {col.headerName}
              </Typography>
            </Box>
          ))}
        </Box>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 350px)',
            width: '100%',
            overflow: 'auto',
          }}
          className="scrollbar"
        >
          {isLoading || isRefetching ? (
            skeletonArray.map((arr, index) => (
              <React.Fragment key={index}>
                <Box display="flex" gap={2} p={2}>
                  <Skeleton width={columns[0].width} height={50} />
                  <Skeleton width={columns[1].width} height={50} />
                  <Skeleton width={columns[2].width} height={50} />
                  <Skeleton width={columns[3].width} height={50} />
                </Box>
                <Divider light />
              </React.Fragment>
            ))
          ) : !isLoading && data === null ? (
            <AdxEmptyStateV2 title="No leave request" />
          ) : (
            data?.data?.map((item, index) => (
              <React.Fragment key={index}>
                <Box
                  onClick={() =>
                    navigate(`/app/leave/request/${item.id}`, {
                      state: {
                        backgroundLocation: location,
                      },
                    })
                  }
                  sx={{
                    '&:hover': {
                      cursor: 'pointer',
                    },
                  }}
                  display="flex"
                  px={2}
                >
                  <Box
                    sx={{
                      width: columns[0].width,
                      py: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', lg: 'row' },
                      alignItems: { xs: 'flex-start', lg: 'center' },
                      columnGap: 3,
                    }}
                  >
                    <Typography variant="subtitle1" color="dark-text.main">
                      {item.leave_policy_name}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: columns[1].width,
                      py: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', lg: 'row' },
                      alignItems: { xs: 'flex-start', lg: 'center' },
                      columnGap: 3,
                      rowGap: isLarge ? 0 : 1,
                    }}
                  >
                    <Typography variant="subtitle 1" color="alternate.main">
                      {item.duration === 1 ? `${item.duration} day` : `${item.duration} days`}
                    </Typography>
                    {isLarge ? (
                      <Icon sx={{ fontSize: 7, color: 'light-gray-text.main' }}>circle</Icon>
                    ) : null}

                    <Typography
                      variant={isLarge ? 'body1' : 'caption'}
                      color="light-gray-text.main"
                    >
                      {`${item.start_date} - ${item.end_date}`}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: columns[2].width,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      columnGap: 3,
                    }}
                  >
                    <AdxStatusBar status={leaveRequestStatusHandler(item.status)} />
                  </Box>
                  <Box
                    sx={{
                      width: columns[3].width,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      columnGap: 3,
                    }}
                  >
                    {isLarge ? (
                      <AdxButton variant={'ghost'} size={'tiny'}>
                        View
                      </AdxButton>
                    ) : (
                      <Icon sx={{ color: 'alternate.main' }}>arrow_forward_ios</Icon>
                    )}
                  </Box>
                </Box>
                <Divider />
              </React.Fragment>
            ))
          )}
        </Box>
        {!isLoading && data !== null && (
          <AdxPaginationNew
            count={data?.total}
            page={currentPage}
            onPageChange={handleChangeCurrentPage}
            rowsPerPage={rowsPerCurrentPage}
            onRowsPerPageChange={handleChangeRowsPerCurrentPage}
          />
        )}
      </Box>
    </>
  )
}

export default AllRequestTable
